import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiProjectBreakdown, ApiLanguage } from './Models/ApiProjectBreakdown';
import { ProjectBreakdown, Language } from './Models/ProjectBreakdown';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';

type ApiResponse = ApiProjectBreakdown | null | undefined;

interface DataResponse {
  data?: ProjectBreakdown;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}

export type ClientOutput = DataResponse | ErrorResponse;

export class ProjectBreakdownClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(repositoryUUID: string): Promise<ClientOutput> {
    if (!isValidUUID(repositoryUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(repositoryUUID);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    const { frameworks, languages, total_bytes, total_files, total_lines } =
      response.metadata;

    return {
      data: {
        frameworks: frameworks,
        languages: languages.map(toLanguage),
        totalBytes: total_bytes,
        totalFiles: total_files,
        totalLines: total_lines,
      },
    };
  }

  private async tryRetrieveAsync(
    repositoryUUID: string
  ): Promise<ApiResponse | HttpError> {
    const href = getHref(repositoryUUID);

    return await this.getter.get(href);
  }
}

export function getHref(repositoryUUID: string): string {
  const uri = URI('/api/v1/projects')
    .segment(repositoryUUID)
    .segment('data')
    .segment('meta');

  return uri.toString();
}

export function toLanguage(language: ApiLanguage): Language {
  return {
    name: language.name,
    numBytes: language.num_bytes,
    numFiles: language.num_files,
    numLines: language.num_lines,
    // Note: The total of all of the languages' percentages may not add up to 1
    percentBytes: language.percent_bytes,
    percentFiles: language.percent_files,
    percentLines: language.percent_lines,
    testBytes: language.test_bytes,
    testFiles: language.test_files,
    testLines: language.test_files,
  };
}
